import { Classes, Dialog, Popover } from "@blueprintjs/core";
import { PlanrButton } from "modules/common/components/planr/button/Button";
import { motion } from "motion/react";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import { DatePicker } from "modules/common/components/form/DatePicker";
import { formatDate } from "modules/common/services/formatting/date";
import { MoneyInput } from "modules/common/components/money/MoneyInput";
import { OrgEmployeeData, OrgUnitData, OrgUnitTypeData } from "../types";
import { AreaField, Field, SelectField, TextField } from "./fields";
import { PlusIcon, UNIT_ICONS, UnitTypeIcon } from "./icons";
import styles from "./OrgTree.module.scss";

/* ---------------- инлайн-форма нового подразделения ---------------- */

interface AddUnitFormProps {
    parentName: string;
    types: OrgUnitTypeData[];
    defaultTypeId: string;
    onSubmit(values: { name: string; unitTypeId: string | null; description: string }): Promise<unknown>;
    onCancel(): void;
    /** введено ли что-то: закрыть такую форму можно только с подтверждением */
    onDirtyChange(dirty: boolean): void;
}

export const AddUnitForm = ({
    parentName,
    types,
    defaultTypeId,
    onSubmit,
    onCancel,
    onDirtyChange,
}: AddUnitFormProps) => {
    const [name, setName] = useState("");
    const [typeId, setTypeId] = useState(defaultTypeId);
    const [description, setDescription] = useState("");
    const [busy, setBusy] = useState(false);
    const ref = useRef<HTMLInputElement>(null);
    const dirty = !!name.trim() || !!description.trim() || typeId !== defaultTypeId;

    useEffect(() => {
        const t = setTimeout(() => ref.current?.focus(), 60);
        return () => clearTimeout(t);
    }, []);

    useEffect(() => {
        onDirtyChange(dirty);
    }, [dirty, onDirtyChange]);
    useEffect(() => () => onDirtyChange(false), [onDirtyChange]);

    const submit = async () => {
        if (!name.trim()) {
            return;
        }
        setBusy(true);
        const ok = await onSubmit({ name: name.trim(), unitTypeId: typeId || null, description: description.trim() });
        setBusy(false);
        if (ok) {
            onCancel();
        }
    };

    return (
        <form
            className={styles.inlineForm}
            onSubmit={(e) => {
                e.preventDefault();
                submit();
            }}
        >
            <h4>Новое подразделение в «{parentName}»</h4>
            <Field label="Наименование">
                <TextField value={name} onChange={setName} inputRef={ref} />
            </Field>
            <Field label="Тип узла">
                <SelectField
                    value={typeId}
                    emptyLabel="Подразделение"
                    options={types.map((t) => ({ id: t.id, label: t.name }))}
                    onChange={setTypeId}
                />
            </Field>
            <Field label="Примечания" hint="Руководителя назначите в карточке, когда в узле появятся сотрудники">
                <AreaField value={description} onChange={setDescription} />
            </Field>
            <div className={styles.formRow}>
                <PlanrButton type="graybtn" size="small" onClick={onCancel}>
                    Отмена
                </PlanrButton>
                <PlanrButton type="greenish" size="small" nativeType="submit" disabled={busy || !name.trim()}>
                    Добавить
                </PlanrButton>
            </div>
        </form>
    );
};

/**
 * Всплывающая карточка с формой у кнопки «+ Подразделение»: справа от кнопки, не выходит
 * за экран, закрывается кликом мимо и Esc. Своя, а не Popover Blueprint: тому нужна цель
 * в разметке, а кнопка живёт внутри холста и двигается вместе с ним.
 */
export const AnchoredPopover = ({
    anchor,
    children,
    onClose,
}: {
    anchor: HTMLElement;
    children: React.ReactNode;
    onClose(): void;
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const rect = anchor.getBoundingClientRect();
    const width = 300;
    const left = Math.min(rect.right + 10, window.innerWidth - width - 12);
    const [top, setTop] = useState(Math.max(12, rect.top - 12));

    // по реальной высоте формы: не вылезать за низ экрана
    useLayoutEffect(() => {
        const height = ref.current?.offsetHeight ?? 0;
        setTop(Math.max(12, Math.min(rect.top - 12, window.innerHeight - height - 12)));
        // позиция считается один раз при открытии
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const onDown = (e: MouseEvent) => {
            // выпадающие списки формы рисуются в портале Blueprint — клик в них не «мимо»
            if ((e.target as HTMLElement).closest?.(".bp6-portal")) {
                return;
            }
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        // Esc в окне поверх формы (подтверждение) закрывает это окно, а не форму
        const onKey = (e: KeyboardEvent) =>
            e.key === "Escape" && !(e.target as HTMLElement).closest?.(".bp6-portal") && onClose();
        // клик, который открыл форму, не должен её же закрыть. Слушаем на погружении: холст
        // React Flow (d3-zoom) останавливает всплытие mousedown, и клик по дереву форму не закрывал
        const t = setTimeout(() => document.addEventListener("mousedown", onDown, true), 0);
        document.addEventListener("keydown", onKey);
        return () => {
            clearTimeout(t);
            document.removeEventListener("mousedown", onDown, true);
            document.removeEventListener("keydown", onKey);
        };
    }, [onClose]);

    return createPortal(
        <motion.div
            ref={ref}
            className={styles.floating}
            style={{ left, top, width }}
            initial={{ opacity: 0, x: -8, scale: 0.97 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            transition={{ type: "spring", stiffness: 420, damping: 32 }}
        >
            {children}
        </motion.div>,
        document.body
    );
};

/* ---------------- закрыть без сохранения ---------------- */

/** Вопрос перед тем, как закрыть форму или карточку с введёнными, но не сохранёнными данными */
export const DiscardChangesDialog = ({ onDiscard, onStay }: { onDiscard(): void; onStay(): void }) => (
    <Dialog
        isOpen
        title="Несохранённые изменения"
        onClose={onStay}
        className="figma-dialog"
        backdropClassName="standard"
        style={{ width: 420 }}
    >
        <div className={Classes.DIALOG_BODY}>
            <p>Введённые данные не сохранены и пропадут. Закрыть без сохранения?</p>
        </div>
        <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                <PlanrButton type="graybtn" size="small" onClick={onStay}>
                    Вернуться
                </PlanrButton>
                <PlanrButton type="danger" size="small" onClick={onDiscard}>
                    Закрыть без сохранения
                </PlanrButton>
            </div>
        </div>
    </Dialog>
);

/* ---------------- перевод сотрудника перетаскиванием ---------------- */

interface MoveEmployeeDialogProps {
    employee: OrgEmployeeData;
    from: OrgUnitData | null;
    to: OrgUnitData;
    isManager: boolean;
    /** можно сразу сменить должность */
    canChangePosition: boolean;
    loadPositions(): Promise<{ id: string; label: string }[]>;
    /** право на штатку: можно сразу задать новые оклад и премию */
    canSalary: boolean;
    loadConditions(employerId: string): Promise<any[]>;
    onMove(
        date: string,
        positionId: string | null,
        pay: { salary: number; bonus: number } | null
    ): Promise<unknown>;
    onClose(): void;
}

const nextMonthStart = () => moment().add(1, "month").startOf("month").toDate();

/**
 * Перенос сотрудника в другое подразделение - перевод с даты (задним числом нельзя), можно сразу
 * на другую должность. Оклад и премия не меняются; ранг на той же должности остаётся, на новой -
 * ранг с тем же названием, если он есть, иначе сбрасывается
 */
export const MoveEmployeeDialog = ({
    employee,
    from,
    to,
    isManager,
    canChangePosition,
    loadPositions,
    canSalary,
    loadConditions,
    onMove,
    onClose,
}: MoveEmployeeDialogProps) => {
    const [busy, setBusy] = useState(false);
    const [date, setDate] = useState<Date | null>(nextMonthStart());
    const [positions, setPositions] = useState<{ id: string; label: string }[]>([]);
    const [positionId, setPositionId] = useState(employee.positionId ?? "");
    const [conditions, setConditions] = useState<any[]>([]);
    const [salary, setSalary] = useState<number | null>(null);
    const [bonus, setBonus] = useState<number | null>(null);

    useEffect(() => {
        if (canSalary) {
            loadConditions(employee.id).then(setConditions);
        }
    }, [canSalary, loadConditions, employee.id]);

    // оклад и премия - действующие на месяц перевода
    useEffect(() => {
        if (!canSalary || !date) {
            return;
        }
        const month = moment(date).startOf("month");
        const parse = (since: string) => moment(since, ["DD.MM.YYYY", moment.ISO_8601], true);
        const current = [...conditions]
            .filter((r) => !parse(r.since).isAfter(month, "month"))
            .sort((a, b) => parse(b.since).valueOf() - parse(a.since).valueOf())[0];
        setSalary(current ? current.salary : 0);
        setBonus(current ? current.bonus : 0);
    }, [conditions, date, canSalary]);

    useEffect(() => {
        if (canChangePosition) {
            loadPositions().then(setPositions);
        }
    }, [canChangePosition, loadPositions]);

    const today = moment().startOf("day");
    const dateError = !date ? "Укажите дату перевода" : moment(date).isBefore(today) ? "Задним числом перевести нельзя" : "";

    const move = async () => {
        if (!date || dateError) {
            return;
        }
        setBusy(true);
        await onMove(
            formatDate(date),
            positionId && positionId !== employee.positionId ? positionId : null,
            canSalary ? { salary: salary ?? 0, bonus: bonus ?? 0 } : null
        );
        setBusy(false);
        onClose();
    };

    return (
        <Dialog
            isOpen
            title="Перевод сотрудника"
            onClose={onClose}
            className="figma-dialog"
            backdropClassName="standard"
            style={{ width: 520 }}
        >
            <div className={Classes.DIALOG_BODY}>
                <p>
                    {employee.name}
                    {employee.positionName ? `, ${employee.positionName}` : ""}
                </p>
                <p>{from ? `Из «${from.name}» в «${to.name}».` : `В «${to.name}».`}</p>

                <Field label="С даты">
                    <DatePicker
                        clasName="planr-default-input"
                        value={date}
                        minDate={today.toDate()}
                        onChange={(value) => setDate(value)}
                        small
                    />
                    {dateError && <span className={styles.notice}>{dateError}</span>}
                </Field>

                {canChangePosition && (
                    <Field
                        label="Должность"
                        hint="Ранг на новой должности сохранится, если у неё есть ранг с тем же названием, иначе сбросится"
                    >
                        <SelectField
                            value={positionId}
                            options={positions}
                            onChange={setPositionId}
                            filterable
                        />
                    </Field>
                )}

                {canSalary ? (
                    <div className={styles.payRow}>
                        <Field label="Оклад">
                            <MoneyInput
                                className="planr-default-input"
                                value={salary ?? ""}
                                onMoneyChange={(money) => setSalary(money.floatValue ?? 0)}
                            />
                        </Field>
                        <Field label="Премия">
                            <MoneyInput
                                className="planr-default-input"
                                value={bonus ?? ""}
                                onMoneyChange={(money) => setBonus(money.floatValue ?? 0)}
                            />
                        </Field>
                    </div>
                ) : null}

                <p className={styles.hint}>
                    {canSalary
                        ? "Оклад и премия действуют с месяца перевода и сразу попадают в штатное расписание."
                        : "Оклад и премия не меняются."}{" "}
                    Перевод попадёт в историю структуры.
                </p>

                {isManager && (
                    <div className={styles.notice}>
                        Сотрудник — руководитель «{from?.name}». Если он окажется вне этого подразделения, поле
                        «Руководитель» очистится.
                    </div>
                )}
            </div>
            <div className={Classes.DIALOG_FOOTER}>
                <div className={Classes.DIALOG_FOOTER_ACTIONS}>
                    <PlanrButton type="graybtn" size="small" onClick={onClose} disabled={busy}>
                        Отмена
                    </PlanrButton>
                    <PlanrButton type="greenish" size="small" disabled={busy || !!dateError} onClick={move}>
                        Перевести
                    </PlanrButton>
                </div>
            </div>
        </Dialog>
    );
};

/* ---------------- справочник типов узлов ---------------- */

interface TypesDialogProps {
    types: OrgUnitTypeData[];
    usage: Map<string, number>;
    onSave(type: Partial<OrgUnitTypeData>): Promise<unknown>;
    onDelete(id: string): Promise<unknown>;
    onClose(): void;
}

export const TypesDialog = ({ types, usage, onSave, onDelete, onClose }: TypesDialogProps) => {
    const [drafts, setDrafts] = useState<Record<string, string>>({});
    const [newName, setNewName] = useState("");
    const [newIcon, setNewIcon] = useState("division");

    const commit = async (type: OrgUnitTypeData) => {
        const name = (drafts[type.id] ?? type.name).trim();
        if (name && name !== type.name) {
            await onSave({ ...type, name });
        }
        setDrafts((d) => {
            const next = { ...d };
            delete next[type.id];
            return next;
        });
    };

    const add = async () => {
        if (!newName.trim()) {
            return;
        }
        const ok = await onSave({ name: newName.trim(), icon: newIcon });
        if (ok) {
            setNewName("");
        }
    };

    return (
        <Dialog
            isOpen
            title="Типы подразделений"
            onClose={onClose}
            className="figma-dialog"
            backdropClassName="standard"
            style={{ width: 520 }}
        >
            <div className={Classes.DIALOG_BODY}>
                <p className={styles.hint}>
                    Тип задаёт подпись и иконку узла в дереве. На вложенность не влияет: узел любого типа может
                    содержать узел любого другого типа.
                </p>
                <div className={styles.typesList}>
                    {types.map((t) => (
                        <div key={t.id} className={styles.typeRow}>
                            <IconPicker value={t.icon} onChange={(icon) => onSave({ ...t, icon })} />
                            <input
                                className="bp6-input planr-default-input"
                                value={drafts[t.id] ?? t.name}
                                maxLength={60}
                                onChange={(e) => setDrafts((d) => ({ ...d, [t.id]: e.target.value }))}
                                onBlur={() => commit(t)}
                                onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                            />
                            <PlanrButton
                                type="lightdanger"
                                size="small"
                                icon="general-trash"
                                round
                                disabled={(usage.get(t.id) ?? 0) > 0}
                                title={
                                    (usage.get(t.id) ?? 0) > 0
                                        ? `Назначен подразделениям: ${usage.get(t.id)}`
                                        : "Удалить тип"
                                }
                                onClick={() => onDelete(t.id)}
                            />
                        </div>
                    ))}
                    <div className={styles.typeRow}>
                        <IconPicker value={newIcon} onChange={setNewIcon} />
                        <input
                            className="bp6-input planr-default-input"
                            value={newName}
                            placeholder="Новый тип, например «Лаборатория»"
                            maxLength={60}
                            onChange={(e) => setNewName(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && add()}
                        />
                        <PlanrButton type="greenish" size="small" round disabled={!newName.trim()} onClick={add}>
                            <PlusIcon size={12} />
                        </PlanrButton>
                    </div>
                </div>
            </div>
        </Dialog>
    );
};

const IconPicker = ({ value, onChange }: { value: string; onChange(icon: string): void }) => {
    const [open, setOpen] = useState(false);
    return (
        <Popover
            isOpen={open}
            onInteraction={setOpen}
            placement="bottom-start"
            content={
                <div className={styles.iconGrid}>
                    {UNIT_ICONS.map((i) => (
                        <button
                            key={i.key}
                            type="button"
                            className={styles.iconPick}
                            title={i.label}
                            onClick={() => {
                                onChange(i.key);
                                setOpen(false);
                            }}
                        >
                            <UnitTypeIcon name={i.key} size={16} />
                        </button>
                    ))}
                </div>
            }
        >
            <button type="button" className={styles.iconPick} title="Иконка">
                <UnitTypeIcon name={value} size={16} />
            </button>
        </Popover>
    );
};
